/**
 * Email Identity — sender profile and delivery infrastructure for this chambers.
 * Route: /barrister/settings/email-identity
 *
 * Controls:
 *   - From address displayed on fee notes, document sends, intake confirmations
 *   - Reply capture: inbound solicitor replies are threaded back into the matter
 *   - Domain verification guidance
 *
 * Phase 2 (not built): Gmail/Outlook OAuth mailbox sync.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  EnvelopeSimple,
  Warning,
  CheckCircle,
  CircleNotch,
  ArrowBendUpLeft,
  Globe,
  Lock,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  fetchSenderProfile,
  updateSenderProfile,
  fetchChambersPrefs,
  type SenderProfileData,
} from '@/lib/meApi';

// ─── Shared primitives ────────────────────────────────────────

const inputCls =
  'w-full rounded-xl border border-border/70 bg-background px-3.5 py-2.5 text-sm text-foreground focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 transition-all disabled:opacity-50';

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </label>
      {children}
      {hint && <p className="text-xs text-muted-foreground/70">{hint}</p>}
    </div>
  );
}

const SENDER_OPTIONS: { value: SenderProfileData['preferredSender']; label: string; desc: string }[] = [
  {
    value: 'system',
    label: 'Jurivoxa system address',
    desc: 'Sent from noreply@jurivoxa.app — always works, no setup needed.',
  },
  {
    value: 'professional',
    label: 'Professional email',
    desc: 'Your chambers professional address (e.g. clerks@danielmercerkc.co.uk). Must be verified with your email provider before use.',
  },
  {
    value: 'clerk',
    label: 'Clerk email',
    desc: 'A dedicated clerk or administration address. Must be verified before use.',
  },
];

// ─── Page ─────────────────────────────────────────────────────

export default function EmailIdentityPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['sender-profile'],
    queryFn: fetchSenderProfile,
    staleTime: 60_000,
  });

  const { data: chambers } = useQuery({
    queryKey: ['me-chambers'],
    queryFn: fetchChambersPrefs,
    staleTime: 60_000,
  });

  const chambersEmailMissing = !isLoading && chambers && !chambers.email;

  const [form, setForm] = useState<Partial<SenderProfileData>>({});
  const [saving, setSaving] = useState(false);

  const current = {
    displayName:       form.displayName       ?? data?.displayName       ?? '',
    professionalEmail: form.professionalEmail ?? data?.professionalEmail ?? '',
    clerkEmail:        form.clerkEmail        ?? data?.clerkEmail        ?? '',
    preferredSender:   form.preferredSender   ?? data?.preferredSender   ?? 'system',
    replyTo:           form.replyTo           ?? data?.replyTo           ?? '',
  };

  const isDirty = Object.keys(form).length > 0;

  function set<K extends keyof SenderProfileData>(k: K, v: SenderProfileData[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  const handleSave = async () => {
    if (!isDirty) return;
    setSaving(true);
    try {
      const payload: Partial<SenderProfileData> = { ...form };
      if (payload.displayName === '') payload.displayName = null;
      if (payload.professionalEmail === '') payload.professionalEmail = null;
      if (payload.clerkEmail === '') payload.clerkEmail = null;
      if (payload.replyTo === '') payload.replyTo = null;
      await updateSenderProfile(payload);
      qc.invalidateQueries({ queryKey: ['sender-profile'] });
      setForm({});
      toast.success('Email identity saved');
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message ?? 'Failed to save email identity');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <CircleNotch className="h-6 w-6 animate-spin text-muted-foreground/40" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <PageHeader
        onBack={() => navigate(-1)}
        title="Email identity"
        subtitle="Control how your chambers appears on outbound email — fee notes, document sends, intake confirmations."
      />

      <div className="space-y-6">
        {/* Preflight: chambers email not configured — professional sender will fail */}
        {chambersEmailMissing && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3.5 dark:border-red-800/50 dark:bg-red-900/10">
            <div className="flex items-start gap-3">
              <Warning className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-400" weight="fill" />
              <div className="space-y-1">
                <p className="text-sm font-semibold text-red-900 dark:text-red-200">
                  Chambers email address not configured
                </p>
                <p className="text-sm text-red-800/80 dark:text-red-300/70">
                  A professional sender address requires your chambers email to be set first.{' '}
                  <Link
                    to="/barrister/settings/chambers"
                    className="font-medium underline underline-offset-2"
                  >
                    Go to Practice Settings
                  </Link>{' '}
                  to add it, then return here.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Domain status banner */}
        {current.preferredSender !== 'system' && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5 dark:border-amber-800/50 dark:bg-amber-900/10">
            <div className="flex items-start gap-3">
              <Warning className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" weight="fill" />
              <div className="space-y-1">
                <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                  Domain verification required
                </p>
                <p className="text-xs text-amber-800/80 dark:text-amber-300/80">
                  Custom domain sending requires the domain to be verified in your email
                  delivery provider before use. Until verified, outbound emails will
                  silently fall back to the Jurivoxa system address. To verify, log in
                  to your Resend dashboard → Domains and add the required DNS records.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Display information */}
        <Card>
          <CardContent className="space-y-5 p-6">
            <div className="flex items-center gap-2 border-b border-border pb-4">
              <EnvelopeSimple className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-semibold text-foreground">Sender information</span>
            </div>

            <Field
              label="Display name"
              hint="Appears as the sender name in email clients. Leave blank to use the chambers name."
            >
              <input
                className={inputCls}
                value={current.displayName ?? ''}
                onChange={(e) => set('displayName', e.target.value || null)}
                placeholder="e.g. Daniel Mercer KC Chambers"
                maxLength={200}
              />
            </Field>

            <Field
              label="Reply-to address"
              hint="Where replies land. Defaults to the From address if left blank."
            >
              <input
                className={inputCls}
                value={current.replyTo ?? ''}
                onChange={(e) => set('replyTo', e.target.value || null)}
                placeholder="e.g. clerks@danielmercerkc.co.uk"
                type="email"
                maxLength={254}
              />
            </Field>
          </CardContent>
        </Card>

        {/* From address */}
        <Card>
          <CardContent className="space-y-5 p-6">
            <div className="flex items-center gap-2 border-b border-border pb-4">
              <span className="text-sm font-semibold text-foreground">Outbound From address</span>
            </div>

            <div className="space-y-2.5">
              {SENDER_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => set('preferredSender', opt.value)}
                  className={`flex w-full items-start gap-3 rounded-lg border p-4 text-left transition ${
                    current.preferredSender === opt.value
                      ? 'border-brand-500 bg-brand-50/60'
                      : 'border-border hover:border-brand-200'
                  }`}
                >
                  <div
                    className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition ${
                      current.preferredSender === opt.value
                        ? 'border-brand-500 bg-brand-500'
                        : 'border-border'
                    }`}
                  >
                    {current.preferredSender === opt.value && (
                      <div className="h-1.5 w-1.5 rounded-full bg-white" />
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground">{opt.label}</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">{opt.desc}</div>
                  </div>
                </button>
              ))}
            </div>

            {current.preferredSender !== 'system' && (
              <div className="rounded-md border border-amber-100 bg-amber-50 dark:bg-amber-500/10 px-4 py-3 text-xs text-amber-900 dark:text-amber-200">
                <Warning className="mr-1.5 inline h-3.5 w-3.5" weight="fill" />
                Custom domain sending requires the domain to be verified in your email delivery provider (Resend or SMTP server). Using an unverified domain will result in rejected messages. Contact your system administrator.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Custom address fields */}
        <Card>
          <CardContent className="space-y-5 p-6">
            <div className="flex items-center gap-2 border-b border-border pb-4">
              <span className="text-sm font-semibold text-foreground">Custom addresses</span>
            </div>

            <Field
              label="Professional email"
              hint='Used when "Professional email" is selected above. Must match a verified sender domain.'
            >
              <input
                className={inputCls}
                value={current.professionalEmail ?? ''}
                onChange={(e) => set('professionalEmail', e.target.value || null)}
                placeholder="e.g. clerks@danielmercerkc.co.uk"
                type="email"
                maxLength={254}
              />
            </Field>

            <Field
              label="Clerk / administration email"
              hint='Used when "Clerk email" is selected above.'
            >
              <input
                className={inputCls}
                value={current.clerkEmail ?? ''}
                onChange={(e) => set('clerkEmail', e.target.value || null)}
                placeholder="e.g. admin@danielmercerkc.co.uk"
                type="email"
                maxLength={254}
              />
            </Field>
          </CardContent>
        </Card>

        {/* Reply capture */}
        <Card>
          <CardContent className="space-y-4 p-6">
            <div className="flex items-center gap-2 border-b border-border pb-4">
              <ArrowBendUpLeft className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-semibold text-foreground">Reply capture</span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              When Jurivoxa sends a document or fee note to a solicitor, the
              outbound email sets a unique <code className="rounded bg-muted px-1 py-0.5 text-xs font-mono">Reply-To</code> address
              on each message. When the solicitor clicks Reply, their response is
              automatically captured inside the matter — no Gmail or Outlook
              integration required.
            </p>
            <div className="rounded-lg border border-border/60 bg-muted/30 p-4 space-y-2">
              <div className="flex items-center gap-2.5">
                <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
                <div>
                  <p className="text-xs font-semibold text-foreground">Infrastructure required</p>
                  <p className="text-xs text-muted-foreground">
                    Reply capture requires an inbound email domain (e.g.{' '}
                    <code className="rounded bg-muted px-1 py-0.5 font-mono">inbound.danielmercerkc.co.uk</code>)
                    with MX records pointing to an inbound email provider such as
                    Mailgun, Postmark, or SendGrid. Contact your system administrator
                    to configure <code className="rounded bg-muted px-1 py-0.5 font-mono">INBOUND_EMAIL_DOMAIN</code>.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <Lock className="h-4 w-4 text-muted-foreground shrink-0" />
                <div>
                  <p className="text-xs font-semibold text-foreground">Future: Gmail / Outlook sync</p>
                  <p className="text-xs text-muted-foreground">
                    Full OAuth mailbox sync (all inbound email, not just replies) is
                    planned for a future release and will use the same thread model.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save bar */}
        <div className="flex items-center justify-end gap-3 border-t border-border pt-4">
          {isDirty && (
            <button
              className="text-xs text-muted-foreground hover:text-foreground"
              onClick={() => setForm({})}
            >
              Discard changes
            </button>
          )}
          <Button
            variant="primary"
            size="md"
            disabled={!isDirty || saving}
            onClick={handleSave}
            leftIcon={saving ? <CircleNotch className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5" />}
          >
            {saving ? 'Saving…' : 'Save identity'}
          </Button>
        </div>
      </div>
    </div>
  );
}
